import React, { useMemo } from 'react';
import { Button } from '../ui/button';
import { 
  CheckCircle2, 
  AlertTriangle, 
  Package, 
  MapPin,
  Layers,
  Scale,
  Ruler,
  Truck,
  AlertCircle,
  Info,
  XCircle,
  Image as ImageIcon,
  ArrowDown,
  ShieldAlert,
  Utensils,
  Pill,
  Box
} from 'lucide-react';

/**
 * LoadingInstructionPanel - Shows detailed instructions for loading the next pallet
 * 
 * Features:
 * - Large pallet image for easy identification
 * - Clear placement position (lane, row, stacking)
 * - Handling tips based on attributes and stacking class
 * - Quick issue marking
 * - Load confirmation
 */
export default function LoadingInstructionPanel({
  pallet,
  allPallets = [],
  placedPallets = [],
  truckDimensions = { width: 2.45, depth: 12, height: 2.6 },
  onLoadConfirm,
  onMarkIssue,
  disabled = false
}) {
  // Get pallet image - must be before any early returns
  const palletImage = pallet?.imageUrl || pallet?.image_url || 
                      pallet?.labelImageUrl || pallet?.label_image_url || null;

  // Calculate position description - must be before any early returns
  const positionInfo = useMemo(() => {
    if (!pallet?.position || !Array.isArray(pallet.position)) {
      return { lane: 'Unknown', row: 'Unknown', description: 'Position not determined' };
    }

    const [x, y, z] = pallet.position;
    const truckWidth = truckDimensions.width || 2.45;
    const truckDepth = truckDimensions.depth || 12;
    const halfWidth = truckWidth / 2;
    const halfDepth = truckDepth / 2;

    // Determine lane (Left, Center, Right)
    let lane;
    if (x < -halfWidth / 3) {
      lane = 'Left Lane';
    } else if (x > halfWidth / 3) {
      lane = 'Right Lane';
    } else {
      lane = 'Center Lane';
    }

    // Determine row position (Back, Middle, Front)
    let row;
    const relativeZ = (z + halfDepth) / truckDepth; // 0 = back, 1 = front
    if (relativeZ < 0.33) {
      row = 'Back (near door)';
    } else if (relativeZ < 0.66) {
      row = 'Middle';
    } else {
      row = 'Front (cab end)';
    }

    // Calculate height level
    const heightLevel = y > 1.5 ? 'High stack' : y > 0.5 ? 'Second level' : 'Floor level';

    return { lane, row, heightLevel, x, y, z };
  }, [pallet?.position, truckDimensions]);

  // Find pallets this one stacks on - must be before any early returns
  const stackingInfo = useMemo(() => {
    if (!pallet?.position || !Array.isArray(pallet.position)) return null;

    const [px, py, pz] = pallet.position;
    const palletHeight = pallet.dimensions?.height || 1.0;
    
    // If pallet is near floor level, it's not stacking on anything
    if (py < palletHeight * 0.8) return null;

    // Find pallets below this one
    const palletsBelow = allPallets.filter(p => {
      if (p.id === pallet.id) return false;
      if (!p.position || !Array.isArray(p.position)) return false;
      
      const [ox, oy, oz] = p.position;
      const otherHeight = p.dimensions?.height || 1.0;
      
      // Check if position overlaps horizontally and is below
      const xOverlap = Math.abs(px - ox) < (pallet.dimensions?.width || 0.8) * 0.8;
      const zOverlap = Math.abs(pz - oz) < (pallet.dimensions?.depth || 1.2) * 0.8;
      const isBelow = oy + otherHeight / 2 < py - palletHeight / 2 + 0.1;
      
      return xOverlap && zOverlap && isBelow;
    });

    if (palletsBelow.length === 0) return null;

    // Sort by height (highest first - closest to our pallet)
    palletsBelow.sort((a, b) => (b.position?.[1] || 0) - (a.position?.[1] || 0));
    
    return {
      palletsBelow,
      directlyOn: palletsBelow[0]
    };
  }, [pallet, allPallets]);

  // Generate handling tips based on attributes and stacking class - must be before any early returns
  const handlingTips = useMemo(() => {
    if (!pallet) return [];
    
    const tips = [];
    const attribute = pallet.attribute || 'none';
    const stackingClass = pallet.stacking_class || pallet.stackingClass || 'C1';

    // Attribute-based tips
    if (attribute === 'dangerous') {
      tips.push({
        type: 'danger',
        icon: <ShieldAlert className="h-4 w-4" />,
        title: 'Dangerous Goods',
        text: 'Handle with extra care. Keep away from food and medicine. Follow ADR regulations. Ensure proper separation distance.',
        color: 'red'
      });
    }

    if (attribute === 'food') {
      tips.push({
        type: 'food',
        icon: <Utensils className="h-4 w-4" />,
        title: 'Food Product',
        text: 'Keep clean and dry. Maintain temperature if cold chain. Do not place near chemicals or dangerous goods.',
        color: 'green'
      });
    }

    if (attribute === 'medicine') {
      tips.push({
        type: 'medicine',
        icon: <Pill className="h-4 w-4" />,
        title: 'Medical Supplies',
        text: 'Handle gently. Temperature sensitive items may require climate control. Keep separate from dangerous goods.',
        color: 'purple'
      });
    }

    // Stacking class tips
    if (stackingClass === 'C0') {
      tips.push({
        type: 'stacking',
        icon: <XCircle className="h-4 w-4" />,
        title: 'No Stacking Allowed',
        text: 'This pallet cannot support any weight on top. Place in final position or ensure nothing is stacked above.',
        color: 'red'
      });
    } else if (stackingClass === 'C1') {
      tips.push({
        type: 'stacking',
        icon: <AlertCircle className="h-4 w-4" />,
        title: 'Light Stacking Only',
        text: 'Can only support light items on top (max 100kg). Handle with care.',
        color: 'yellow'
      });
    } else if (stackingClass === 'C2') {
      tips.push({
        type: 'stacking',
        icon: <Box className="h-4 w-4" />,
        title: 'Medium Stacking',
        text: 'Can support medium weight on top. Standard handling procedures apply.',
        color: 'blue'
      });
    } else if (stackingClass === 'C3') {
      tips.push({
        type: 'stacking',
        icon: <Layers className="h-4 w-4" />,
        title: 'Heavy Duty',
        text: 'Full stacking capacity. Can support heavy loads on top.',
        color: 'green'
      });
    }

    // Not toploadable warning
    if (pallet.notToploadable || pallet.not_toploadable) {
      tips.push({
        type: 'warning',
        icon: <AlertTriangle className="h-4 w-4" />,
        title: 'Not Top-Loadable',
        text: 'Do NOT place any pallets on top of this one! Contents are fragile or unstable.',
        color: 'red'
      });
    }

    // Custom handling tips from scanner
    if (pallet.handling_tips || pallet.handlingTips) {
      tips.push({
        type: 'custom',
        icon: <Info className="h-4 w-4" />,
        title: 'Special Instructions',
        text: pallet.handling_tips || pallet.handlingTips,
        color: 'blue'
      });
    }

    return tips;
  }, [pallet]);

  // Early return after all hooks
  if (!pallet) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center text-gray-400">
          <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">All pallets loaded!</p>
          <p className="text-sm">Or select the next pallet to continue</p>
        </div>
      </div>
    );
  }

  // Issue reasons for quick marking
  const issueOptions = [
    { value: 'missing', label: 'Missing', icon: '❌', color: 'bg-red-500' },
    { value: 'damaged', label: 'Damaged', icon: '💥', color: 'bg-orange-500' },
    { value: 'wrong', label: 'Wrong', icon: '🔄', color: 'bg-yellow-500' },
    { value: 'other', label: 'Other', icon: '❓', color: 'bg-gray-500' },
  ];

  const tipColorClasses = {
    red: 'bg-red-50 border-red-200 text-red-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-700',
  };

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      {/* Header with pallet name */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide opacity-80">Load Next</div>
            <div className="text-lg font-bold truncate">{pallet.name || `Pallet ${pallet.id}`}</div>
          </div>
          <Truck className="h-8 w-8 opacity-50" />
        </div>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        {/* Pallet Image - Large for easy identification */}
        <div className="p-3 border-b">
          {palletImage ? (
            <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden">
              <img 
                src={palletImage} 
                alt={pallet.name || 'Pallet'} 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden absolute inset-0 items-center justify-center bg-gray-100">
                <div className="text-center text-gray-400">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2" />
                  <p className="text-sm">Image failed to load</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="aspect-video bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Package className="h-16 w-16 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No image available</p>
              </div>
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-3 gap-2 p-3 border-b bg-gray-50">
          <div className="text-center">
            <Scale className="h-5 w-5 mx-auto mb-1 text-gray-500" />
            <div className="text-lg font-bold">{pallet.weight || '—'}</div>
            <div className="text-xs text-gray-500">kg</div>
          </div>
          <div className="text-center">
            <Ruler className="h-5 w-5 mx-auto mb-1 text-gray-500" />
            <div className="text-sm font-bold">
              {pallet.dimensions?.width?.toFixed(1)}×{pallet.dimensions?.depth?.toFixed(1)}
            </div>
            <div className="text-xs text-gray-500">m (W×D)</div>
          </div>
          <div className="text-center">
            <Layers className="h-5 w-5 mx-auto mb-1 text-gray-500" />
            <div className="text-sm font-bold">{pallet.dimensions?.height?.toFixed(1) || '—'}</div>
            <div className="text-xs text-gray-500">m height</div>
          </div>
        </div>

        {/* Placement Position */}
        <div className="p-3 border-b">
          <div className="flex items-center space-x-2 mb-2">
            <MapPin className="h-5 w-5 text-blue-600" />
            <span className="font-semibold text-sm">Placement Position</span>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 space-y-1">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Lane:</span>
              <span className="font-medium">{positionInfo.lane}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Row:</span>
              <span className="font-medium">{positionInfo.row}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Level:</span>
              <span className="font-medium">{positionInfo.heightLevel}</span>
            </div>
          </div>
        </div>

        {/* Stacking Info */}
        {stackingInfo && (
          <div className="p-3 border-b">
            <div className="flex items-center space-x-2 mb-2">
              <ArrowDown className="h-5 w-5 text-purple-600" />
              <span className="font-semibold text-sm">Stack On Top Of</span>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="font-medium text-purple-900">
                {stackingInfo.directlyOn?.name || `Pallet ${stackingInfo.directlyOn?.id}`}
              </div>
              {stackingInfo.palletsBelow.length > 1 && (
                <div className="text-xs text-purple-700 mt-1">
                  Stack includes {stackingInfo.palletsBelow.length} pallets below
                </div>
              )}
            </div>
          </div>
        )}

        {/* Handling Tips */}
        <div className="p-3 border-b">
          <div className="flex items-center space-x-2 mb-2">
            <Info className="h-5 w-5 text-amber-600" />
            <span className="font-semibold text-sm">Handling Tips</span>
          </div>
          <div className="space-y-2">
            {handlingTips.map((tip, i) => (
              <div 
                key={i} 
                className={`rounded-lg border p-2 ${tipColorClasses[tip.color]}`}
              >
                <div className="flex items-center space-x-2 mb-1">
                  {tip.icon}
                  <span className="font-medium text-sm">{tip.title}</span>
                </div>
                <p className="text-xs leading-relaxed">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Destination if available */}
        {pallet.destination && (
          <div className="p-3 border-b bg-green-50">
            <div className="text-xs text-green-700 uppercase tracking-wide">Destination</div>
            <div className="font-bold text-green-900">{pallet.destination}</div>
          </div>
        )}
      </div>

      {/* Fixed bottom actions */}
      <div className="flex-shrink-0 border-t bg-white p-3">
        {/* Issue buttons */}
        <div className="grid grid-cols-4 gap-2 mb-3">
          {issueOptions.map(opt => (
            <button
              key={opt.value}
              onClick={() => onMarkIssue?.(pallet.id, opt.value)}
              disabled={disabled}
              className={`p-2 rounded-lg border-2 border-gray-200 hover:border-red-300 hover:bg-red-50 
                         text-center transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <span className="text-lg block">{opt.icon}</span>
              <span className="text-[10px] font-medium text-gray-600">{opt.label}</span>
            </button>
          ))}
        </div>

        {/* Main load button */}
        <Button
          className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
          onClick={() => onLoadConfirm?.(pallet.id)}
          disabled={disabled}
        >
          <CheckCircle2 className="h-6 w-6 mr-2" />
          Confirm Loaded
        </Button>
      </div>
    </div>
  );
}
